import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { LeagueService, TeamService, ContestantService, PredictionService, SeasonService } from '../../services/firebaseService';
import TeamSelector from '../../components/player/TeamSelector';
import { getUserTeamsForLeagueSeason } from '../../utils/teamUtils';
import {
  getEliminationTypeDisplayLabel,
  getRequiredPredictionCount,
  isMultiEliminationWeek,
} from '../../utils/predictionUtils';
import { getTribeColor } from '../../utils/tribeUtils';

/** Compare stored prediction id to contestant id (handles string vs number). */
function predictionIdMatches(stored, contestantId) {
  if (stored === undefined || stored === null || stored === '') {
    return false;
  }
  return String(stored) === String(contestantId);
}

// Reusable component for tribe badges
const TribeBadges = ({ tribes }) => {
  if (!tribes || tribes.length === 0) return null;
  
  // Filter out empty strings and invalid tribe values
  const validTribes = tribes.filter(tribe => tribe && tribe.trim() !== '' && tribe !== 'Select tribe...');
  
  if (validTribes.length === 0) return null;
  
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {validTribes.map((tribe, index) => (
          <div
            key={index}
            className="flex-1 h-2 rounded"
            style={{ backgroundColor: getTribeColor(tribe) }}
          />
        ))}
      </div>
    </div>
  );
};

// Use <button> for reliable taps on mobile; avoid native lazy-loading here — it
// often leaves images blank while scrolling inside nested layouts (Safari/Chrome).
function ContestantTile({ contestant, isSelected, onSelect, className = 'p-4' }) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      aria-label={
        isSelected
          ? `${contestant.name}, selected`
          : `Select ${contestant.name}`
      }
      onClick={() => onSelect(contestant.id)}
      className={`relative w-full text-left rounded-lg border-2 ${className} touch-manipulation select-none transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-survivor-500 focus-visible:ring-offset-2 ${
        isSelected
          ? 'border-survivor-600 bg-survivor-50 shadow-sm ring-2 ring-survivor-500/80 ring-offset-2'
          : 'border-gray-200 bg-white hover:border-survivor-300 active:bg-gray-50'
      }`}
    >
      {isSelected && (
        <span
          className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-survivor-600 text-white shadow-md"
          aria-hidden
        >
          <CheckCircleIcon className="h-5 w-5" />
        </span>
      )}
      <div className="aspect-square mb-3 bg-gray-200 rounded-lg overflow-hidden relative">
        {contestant.imageUrl ? (
          <img
            src={contestant.imageUrl}
            alt=""
            className="absolute inset-x-0 top-0 w-full h-[300%] object-cover object-top rounded-lg pointer-events-none"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-4xl pointer-events-none">👤</div>
        )}
      </div>
      <h3 className="font-medium text-sm text-gray-900 truncate">{contestant.name}</h3>
      {contestant.bio && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{contestant.bio}</p>
      )}
      <TribeBadges tribes={contestant.tribes} />
    </button>
  );
}

function MultiElimPickSummaryBox({ lines, requiredCount }) {
  if (!lines || lines.length === 0) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
      <p className="font-medium text-gray-900">
        Your picks (elimination order)
      </p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        {lines.map(row => (
          <li key={row.pos}>
            <span
              className={
                row.pending
                  ? 'text-gray-500 italic'
                  : 'text-gray-900 font-medium'
              }
            >
              {row.label}
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-xs text-gray-500">
        {requiredCount === 3
          ? 'Pick in order: 1st voted out, then 2nd, then 3rd.'
          : 'Pick in order: 1st voted out, then 2nd.'}
      </p>
    </div>
  );
}

export default function PredictionCollection3() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [userTeamIds, setUserTeamIds] = useState([]);
  
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [submittedByName, setSubmittedByName] = useState('');
  const [predictions, setPredictions] = useState({
    votedOut: '',
    fireMakingLoser: '',
    winner: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Submit Your Predictions';
    return () => {
      document.title = 'Survivor Fantasy League';
    };
  }, []);

  useEffect(() => {
    if (leagueId && seasonId) {
      loadData();
    }
  }, [leagueId, seasonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [leagueData, seasonData, teamsData, contestantsData] = await Promise.all([
        LeagueService.getLeague(leagueId),
        SeasonService.getSeason(seasonId),
        TeamService.getTeamsForLeagueSeason(leagueId, seasonId),
        ContestantService.getContestantsBySeason(seasonId),
      ]);

      if (!leagueData) {
        throw new Error('League not found');
      }
      if (!seasonData) {
        throw new Error('Season not found');
      }

      setLeague(leagueData);
      setSeason(seasonData);

      const sortedTeams = [...teamsData].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setTeams(sortedTeams);

      const sortedContestants = [...contestantsData].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setContestants(sortedContestants);

      const openWeeks =
        seasonData.weeks?.filter(week => week.isOpenForPredictions) || [];
      setWeeks(openWeeks);

      if (currentUser && userProfile && userProfile.teams) {
        const claimedTeams = getUserTeamsForLeagueSeason(
          userProfile.teams,
          leagueId,
          seasonId
        );
        setUserTeamIds(claimedTeams);

        if (claimedTeams.length === 1) {
          setSelectedTeam(claimedTeams[0]);
        }
      }

      if (currentUser && userProfile) {
        setSubmittedByName(
          userProfile.displayName || currentUser.email?.split('@')[0] || ''
        );
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(
        error.message || 'Failed to load data. Please check the URL and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const currentWeek = useMemo(
    () => weeks.find(week => week.weekNumber.toString() === selectedWeek),
    [weeks, selectedWeek]
  );

  const multiElimVotedIds = useMemo(() => {
    if (!currentWeek || !isMultiEliminationWeek(currentWeek)) {
      return [];
    }
    const n = getRequiredPredictionCount(currentWeek);
    const parts = predictions.votedOut.split(',').map(s => s.trim());
    const out = [];
    for (let i = 0; i < n; i++) {
      out.push(parts[i] ?? '');
    }
    return out;
  }, [currentWeek, predictions.votedOut]);

  /** Sticky summary for double/triple weeks — three full grids are easy to lose context in. */
  const multiElimProgressLines = useMemo(() => {
    if (!currentWeek || !isMultiEliminationWeek(currentWeek)) {
      return null;
    }
    const n = getRequiredPredictionCount(currentWeek);
    return Array.from({ length: n }, (_, i) => {
      const id = multiElimVotedIds[i];
      if (!id) {
        return { pos: i + 1, label: 'Not selected yet', pending: true };
      }
      const c = contestants.find(x => String(x.id) === String(id));
      return { pos: i + 1, label: c?.name ?? 'Unknown', pending: false };
    });
  }, [currentWeek, multiElimVotedIds, contestants]);

  const selectVotedOut = useCallback(id => {
    setPredictions(prev => ({ ...prev, votedOut: id }));
  }, []);

  const selectFireMakingLoser = useCallback(id => {
    setPredictions(prev => ({ ...prev, fireMakingLoser: id }));
  }, []);

  const selectWinner = useCallback(id => {
    setPredictions(prev => ({ ...prev, winner: id }));
  }, []);

  const handleMultiElimPick = useCallback(
    (slotIndex, contestantId) => {
      setPredictions(prev => {
        if (!currentWeek || !isMultiEliminationWeek(currentWeek)) {
          return prev;
        }
        const n = getRequiredPredictionCount(currentWeek);
        const parts = prev.votedOut.split(',').map(s => s.trim());
        while (parts.length < n) {
          parts.push('');
        }
        parts[slotIndex] = contestantId;
        return { ...prev, votedOut: parts.slice(0, n).join(',') };
      });
    },
    [currentWeek]
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp.seconds) {
      // Firestore timestamp
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return timestamp.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTeam || !selectedWeek) {
      setError('Please select both a team and an episode.');
      return;
    }

    if (!submittedByName.trim()) {
      setError('Please enter your name to confirm your prediction.');
      return;
    }

    if (!currentWeek) {
      setError('Selected episode not found.');
      return;
    }

    // Validate predictions based on week type
    if (currentWeek.isFinale) {
      if (!predictions.votedOut || !predictions.fireMakingLoser || !predictions.winner) {
        setError('Please make all three predictions for the finale.');
        return;
      }
    } else if (isMultiEliminationWeek(currentWeek)) {
      const need = getRequiredPredictionCount(currentWeek);
      if (!predictions.votedOut) {
        setError(
          `Please make all ${need} predictions for this elimination episode.`
        );
        return;
      }
      const votedOutArray = predictions.votedOut.split(',').map(s => s.trim()).filter(s => s);
      if (votedOutArray.length !== need) {
        setError(
          `Please select exactly ${need} contestants (in elimination order).`
        );
        return;
      }
    } else {
      if (!predictions.votedOut) {
        setError('Please make a prediction for who will be voted out.');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');

      // Prepare prediction data based on week type (already using IDs from tiles)
      let predictionData = {};
      
      if (currentWeek.isFinale) {
        predictionData = {
          votedOut: predictions.votedOut,
          fireMakingLoser: predictions.fireMakingLoser,
          winner: predictions.winner
        };
      } else if (isMultiEliminationWeek(currentWeek)) {
        const votedOutArray = predictions.votedOut.split(',').map(s => s.trim()).filter(s => s);
        predictionData = {
          votedOut: votedOutArray.map((id, idx) => ({
            contestantId: id,
            position: idx + 1,
          })),
        };
      } else {
        predictionData = {
          votedOut: predictions.votedOut
        };
      }

      // Determine submittedBy value
      const submittedBy = currentUser 
        ? currentUser.uid 
        : `Public: ${submittedByName.trim()}`;

      if (import.meta.env.DEV) {
        console.log('Checking for existing prediction:', {
          leagueId,
          seasonId,
          teamId: selectedTeam,
          weekNumber: parseInt(selectedWeek),
        });
      }

      const existingPrediction = await PredictionService.getTeamPrediction(
        leagueId, 
        seasonId, 
        selectedTeam, 
        parseInt(selectedWeek)
      );

      if (import.meta.env.DEV) {
        console.log('Existing prediction found:', existingPrediction);
      }

      if (existingPrediction) {
        if (import.meta.env.DEV) {
          console.log('Updating existing prediction with ID:', existingPrediction.id);
        }
        await PredictionService.updatePrediction(
          existingPrediction.id, 
          {
            predictions: predictionData,
            submittedBy: submittedBy,
            updatedAt: new Date()
          },
          submittedBy,
          currentUser ? currentUser.uid : null
        );
      } else {
        if (import.meta.env.DEV) {
          console.log('Creating new prediction');
        }
        await PredictionService.createPrediction(
          {
            leagueId,
            seasonId,
            teamId: selectedTeam,
            weekNumber: parseInt(selectedWeek),
            predictions: predictionData,
            submittedBy: submittedBy,
            isLocked: false
          },
          submittedBy,
          currentUser ? currentUser.uid : null
        );
      }

      setSuccess(true);
      
      // Reset form
      setSelectedTeam('');
      setSelectedWeek('');
      setSubmittedByName('');
      setPredictions({
        votedOut: '',
        fireMakingLoser: '',
        winner: ''
      });

    } catch (error) {
      console.error('Error submitting prediction:', error);
      setError(error.message || 'Failed to submit prediction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading prediction form...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">Error</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Submit Your Prediction
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {league?.name} - {season?.name}
          </p>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ This page contains spoilers. If you haven't watched the last episode, use{' '}
              <a 
                href={`/predictions/${leagueId}/${seasonId}`}
                className="text-yellow-900 underline hover:text-yellow-700 font-medium"
              >
                this version
              </a>{' '}
              instead.
            </p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Prediction Submitted Successfully!
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  Your prediction has been recorded. You can submit another prediction below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Prediction Form */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team Selection */}
            <div>
              {currentUser && userTeamIds.length > 0 ? (
                // Logged-in user with claimed teams - use TeamSelector
                <TeamSelector
                  teams={teams}
                  userTeamIds={userTeamIds}
                  selectedTeamId={selectedTeam}
                  onTeamChange={setSelectedTeam}
                  leagueName={league?.name}
                />
              ) : (
                // Logged-out user or no claimed teams - show full team dropdown
                <>
                  <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-2">
                    1. What team are you on?
                  </label>
                  <select
                    id="team"
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    required
                  >
                    <option value="">Select your team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {/* Episode Selection */}
            <div>
              <label htmlFor="episode" className="block text-sm font-medium text-gray-700 mb-2">
                2. Which episode are you predicting?
              </label>
              <select
                id="episode"
                value={selectedWeek}
                onChange={(e) => {
                  setSelectedWeek(e.target.value);
                  // Reset predictions when week changes
                  setPredictions({
                    votedOut: '',
                    fireMakingLoser: '',
                    winner: ''
                  });
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                required
              >
                <option value="">Select an episode</option>
                {weeks.map(week => (
                  <option key={week.weekNumber} value={week.weekNumber}>
                    Week {week.weekNumber} - {formatDate(week.airDate)}
                    {week.isFinale && ' (Finale)'}
                    {isMultiEliminationWeek(week) &&
                      ` (${getEliminationTypeDisplayLabel(week)})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Predictions */}
            {selectedWeek && currentWeek && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  3. Make your predictions:
                </label>
                
                {currentWeek.isFinale ? (
                  <div className="space-y-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-3">
                        Who will be voted out?
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {contestants.map(contestant => (
                          <ContestantTile
                            key={contestant.id}
                            contestant={contestant}
                            isSelected={predictionIdMatches(
                              predictions.votedOut,
                              contestant.id
                            )}
                            onSelect={selectVotedOut}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-3">
                        Who will lose the fire-making challenge?
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {contestants.map(contestant => (
                          <ContestantTile
                            key={contestant.id}
                            contestant={contestant}
                            isSelected={predictionIdMatches(
                              predictions.fireMakingLoser,
                              contestant.id
                            )}
                            onSelect={selectFireMakingLoser}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-3">
                        Who will win the season?
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {contestants.map(contestant => (
                          <ContestantTile
                            key={contestant.id}
                            contestant={contestant}
                            isSelected={predictionIdMatches(
                              predictions.winner,
                              contestant.id
                            )}
                            onSelect={selectWinner}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : isMultiEliminationWeek(currentWeek) ? (
                  <div className="space-y-6">
                    <MultiElimPickSummaryBox
                      lines={multiElimProgressLines}
                      requiredCount={getRequiredPredictionCount(currentWeek)}
                    />
                    {Array.from(
                      { length: getRequiredPredictionCount(currentWeek) },
                      (_, i) => {
                        const pos = i + 1;
                        const ordinal =
                          pos === 1 ? 'first' : pos === 2 ? 'second' : 'third';
                        return (
                          <div key={pos}>
                            <label className="block text-sm font-medium text-gray-600 mb-3">
                              Vote out #{pos} ({ordinal} person eliminated):
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {contestants.map(contestant => (
                                <ContestantTile
                                  key={contestant.id}
                                  contestant={contestant}
                                  isSelected={predictionIdMatches(
                                    multiElimVotedIds[i],
                                    contestant.id
                                  )}
                                  onSelect={id =>
                                    handleMultiElimPick(i, id)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-3">
                      Who will be voted out?
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {contestants.map(contestant => (
                        <ContestantTile
                          key={contestant.id}
                          contestant={contestant}
                          isSelected={predictionIdMatches(
                            predictions.votedOut,
                            contestant.id
                          )}
                          onSelect={selectVotedOut}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Name Confirmation - Only show for logged-out users */}
            {!currentUser && (
              <div>
                <label htmlFor="submittedByName" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm your name:
                </label>
                <input
                  type="text"
                  id="submittedByName"
                  value={submittedByName}
                  onChange={(e) => setSubmittedByName(e.target.value)}
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="Enter your name"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {submittedByName.length}/100 characters
                </p>
              </div>
            )}

            {selectedWeek &&
              currentWeek &&
              isMultiEliminationWeek(currentWeek) &&
              multiElimProgressLines && (
                <div className="pt-2">
                  <MultiElimPickSummaryBox
                    lines={multiElimProgressLines}
                    requiredCount={getRequiredPredictionCount(currentWeek)}
                  />
                </div>
              )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting || !selectedTeam || !selectedWeek || (!currentUser && !submittedByName.trim())}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Prediction'}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact your league manager.
          </p>
        </div>
      </div>
    </div>
  );
}
